import { createElement } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiRequestError } from '@/lib/api';
import type { CreateStudentInput, UpdateStudentInput } from '@frappi/shared';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Crown } from 'lucide-react';
import { useUIStore } from '@/stores/ui-store';

export const studentKeys = {
  all: ['students'] as const,
  lists: () => [...studentKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) => [...studentKeys.lists(), filters] as const,
  details: () => [...studentKeys.all, 'detail'] as const,
  detail: (id: string) => [...studentKeys.details(), id] as const,
};

export function useStudents() {
  return useQuery({
    queryKey: studentKeys.lists(),
    queryFn: () => api.students.list(),
  });
}

export function useStudent(id: string) {
  return useQuery({
    queryKey: studentKeys.detail(id),
    queryFn: () => api.students.get(id),
    enabled: !!id,
  });
}

export function useCreateStudent() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('students');

  return useMutation({
    mutationFn: (data: CreateStudentInput) => api.students.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: studentKeys.lists() });
      toast.success(t('studentCreated'));
    },
    onError: (error: Error) => {
      // Check if it's a quota exceeded error
      if (error instanceof ApiRequestError && error.isQuotaExceeded()) {
        toast(t('quotaExceeded.title'), {
          description: t('quotaExceeded.description'),
          icon: createElement(Crown, { className: 'h-5 w-5 text-amber-500' }),
          action: {
            label: t('quotaExceeded.upgrade'),
            onClick: () => { useUIStore.getState().setUpgradeDialogOpen(true); },
          },
          duration: 8000,
        });
        return;
      }
      toast.error(error.message || t('errorCreating'));
    },
  });
}

export function useUpdateStudent() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('students');

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateStudentInput }) =>
      api.students.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: studentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: studentKeys.detail(id) });
      toast.success(t('studentUpdated'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('errorUpdating'));
    },
  });
}

export function useDeleteStudent() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('students');

  return useMutation({
    mutationFn: (id: string) => api.students.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: studentKeys.lists() });
      toast.success(t('studentDeleted'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('errorDeleting'));
    },
  });
}
